import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { inr } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Trash2, ShoppingBag, ArrowRight } from "lucide-react";

export default function Cart() {
  const { cart, update, remove } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  const items = cart.items || [];
  const subtotal = cart.subtotal || 0;
  const delivery = subtotal > 999 || subtotal === 0 ? 0 : 49;
  const total = subtotal + delivery;

  if (!user) {
    return (
      <div className="max-w-3xl mx-auto px-5 py-20 text-center" data-testid="cart-signin-required">
        <ShoppingBag className="h-12 w-12 mx-auto text-neutral-500" />
        <h1 className="font-display text-3xl font-bold mt-4">Sign in to view your cart</h1>
        <button className="vm-btn-primary mt-6" onClick={() => navigate("/login")} data-testid="cart-signin-btn">
          Sign in with Google
        </button>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-5 py-20 text-center" data-testid="cart-empty">
        <ShoppingBag className="h-12 w-12 mx-auto text-neutral-500" />
        <h1 className="font-display text-3xl font-bold mt-4">Your cart is empty</h1>
        <p className="text-neutral-400 mt-2">Start shopping to add electronics here.</p>
        <Link to="/shop" className="vm-btn-primary inline-flex items-center gap-2 mt-6" data-testid="cart-shop-now">
          Browse products <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10" data-testid="cart-page">
      <p className="vm-overline">Your cart</p>
      <h1 className="font-display text-4xl md:text-5xl font-bold mt-2 tracking-tighter">Review & checkout</h1>

      <div className="grid lg:grid-cols-12 gap-8 mt-10">
        <div className="lg:col-span-8 space-y-4">
          {items.map((it) => (
            <div key={it.product_id} className="vm-card p-4 flex gap-4" data-testid={`cart-item-${it.product_id}`}>
              <img src={it.image} alt={it.name} className="h-24 w-24 rounded-xl object-cover bg-ink-800" />
              <div className="flex-1 flex flex-col">
                <div className="flex justify-between gap-3">
                  <div>
                    <p className="text-xs text-neutral-500 uppercase tracking-widest font-bold">{it.brand}</p>
                    <h3 className="text-sm font-semibold mt-1 leading-snug">{it.name}</h3>
                  </div>
                  <p className="font-display text-lg font-bold whitespace-nowrap">{inr(it.price * it.quantity)}</p>
                </div>
                <div className="flex items-center justify-between mt-auto pt-3">
                  <div className="flex items-center gap-1 bg-ink-800 rounded-full px-1 py-1">
                    <button
                      className="h-7 w-7 rounded-full hover:bg-white/5"
                      onClick={() => update(it.product_id, it.quantity - 1)}
                      data-testid={`cart-decrease-${it.product_id}`}
                    >−</button>
                    <span className="w-6 text-center text-sm font-mono">{it.quantity}</span>
                    <button
                      className="h-7 w-7 rounded-full hover:bg-white/5"
                      onClick={() => update(it.product_id, it.quantity + 1)}
                      data-testid={`cart-increase-${it.product_id}`}
                    >+</button>
                  </div>
                  <button
                    onClick={async () => { await remove(it.product_id); toast.success("Removed"); }}
                    className="text-xs text-neutral-400 hover:text-blaze-400 flex items-center gap-1"
                    data-testid={`cart-remove-${it.product_id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Remove
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="lg:col-span-4">
          <div className="vm-card p-6 sticky top-24" data-testid="cart-summary">
            <p className="vm-overline">Order summary</p>
            <div className="space-y-3 mt-4 text-sm">
              <div className="flex justify-between"><span className="text-neutral-400">Subtotal</span><span className="font-mono">{inr(subtotal)}</span></div>
              <div className="flex justify-between">
                <span className="text-neutral-400">Delivery</span>
                <span className="font-mono">{delivery === 0 ? <span className="text-green-400">FREE</span> : inr(delivery)}</span>
              </div>
              <div className="vm-divider" />
              <div className="flex justify-between text-lg">
                <span className="font-semibold">Total</span>
                <span className="font-display font-bold">{inr(total)}</span>
              </div>
            </div>
            <button
              className="vm-btn-primary w-full mt-6 flex items-center justify-center gap-2"
              onClick={() => navigate("/checkout")}
              data-testid="cart-checkout-btn"
            >
              Checkout • COD <ArrowRight className="h-4 w-4" />
            </button>
            <p className="text-xs text-neutral-500 mt-3 text-center">Pay on delivery — no card required.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
